import { h, Component } from 'preact'

import * as focusManager from './helpers/focusManager'
import scopeTab from './helpers/scopeTab'
import * as ariaAppHider from './helpers/ariaAppHider'
import * as classList from './helpers/classList'

import portalOpenInstances from './helpers/portalOpenInstances'
import './helpers/bodyTrap'

// so that our CSS is statically analyzable
const CLASS_NAMES = {
  overlay: 'ReactModal__Overlay',
  content: 'ReactModal__Content',
}

const TAB_KEY = 9
const ESC_KEY = 27

let ariaHiddenInstances = 0

export const bodyOpenClassName = 'modal-body--open'

export default class ModalPortal extends Component {
  static defaultProps = {
    style: {
      overlay: {},
      content: {},
    },
    defaultStyles: {},
  }

  constructor(props) {
    super(props)

    this.state = {
      afterOpen: false,
      beforeClose: false,
    }

    this.shouldClose = null
    this.moveFromContentToOverlay = null
  }

  componentDidMount() {
    if (this.props.isOpen) {
      this.open()
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.isOpen && !prevProps.isOpen) {
      this.open()
    } else if (!this.props.isOpen && prevProps.isOpen) {
      this.close()
    }

    // Focus only needs to be set once when the modal is being opened
    if (this.state.isOpen && !prevState.isOpen) {
      this.focusContent()
    }
  }

  componentWillUnmount() {
    if (this.state.isOpen) {
      this.afterClose()
    }
    clearTimeout(this.closeTimer)
    cancelAnimationFrame(this.openAnimationFrame)
  }

  setOverlayRef = (overlay) => {
    this.overlay = overlay
    this.props.overlayRef && this.props.overlayRef(overlay)
  }

  setContentRef = (content) => {
    this.content = content
    this.props.contentRef && this.props.contentRef(content)
  }

  beforeOpen() {
    const { appElement, htmlOpenClassName } = this.props

    // Add classes.
    bodyOpenClassName && classList.add(document.body, bodyOpenClassName)

    htmlOpenClassName &&
      classList.add(document.getElementsByTagName('html')[0], htmlOpenClassName)

    ariaHiddenInstances += 1
    ariaAppHider.hide(appElement)

    portalOpenInstances.register(this)
  }

  afterClose = () => {
    const { appElement, htmlOpenClassName } = this.props

    // Remove classes.
    bodyOpenClassName && classList.remove(document.body, bodyOpenClassName)

    htmlOpenClassName &&
      classList.remove(
        document.getElementsByTagName('html')[0],
        htmlOpenClassName,
      )

    // Reset aria-hidden attribute if all modals have been removed
    if (ariaHiddenInstances > 0) {
      ariaHiddenInstances -= 1

      if (ariaHiddenInstances === 0) {
        ariaAppHider.show(appElement)
      }
    }

    focusManager.returnFocus(false)
    focusManager.teardownScopedFocus()

    if (this.props.onAfterClose) {
      this.props.onAfterClose()
    }

    portalOpenInstances.deregister(this)
  }

  open = () => {
    this.beforeOpen()
    if (this.state.afterOpen && this.state.beforeClose) {
      clearTimeout(this.closeTimer)
      this.setState({ beforeClose: false })
    } else {
      focusManager.setupScopedFocus(this.node)
      focusManager.markForFocusLater()

      this.setState({ isOpen: true }, () => {
        this.openAnimationFrame = requestAnimationFrame(() => {
          this.setState({ afterOpen: true })

          if (this.props.isOpen && this.props.onAfterOpen) {
            this.props.onAfterOpen({
              overlayEl: this.overlay,
              contentEl: this.content,
            })
          }
        })
      })
    }
  }

  close = () => {
    this.closeWithoutTimeout()
  }

  // Don't steal focus from inner elements
  focusContent = () =>
    this.content &&
    !this.contentHasFocus() &&
    this.content.focus({ preventScroll: true })

  closeWithTimeout = () => {
    const closesAt = Date.now() + this.props.closeTimeoutMS
    this.setState({ beforeClose: true, closesAt }, () => {
      this.closeTimer = setTimeout(
        this.closeWithoutTimeout,
        this.state.closesAt - Date.now(),
      )
    })
  }

  closeWithoutTimeout = () => {
    this.setState(
      {
        beforeClose: false,
        isOpen: false,
        afterOpen: false,
        closesAt: null,
      },
      this.afterClose,
    )
  }

  handleKeyDown = (event) => {
    if (event.keyCode === TAB_KEY) {
      scopeTab(this.content, event)
    }

    if (event.keyCode === ESC_KEY) {
      event.stopPropagation()
      this.requestClose(event)
    }
  }

  handleOverlayOnClick = (event) => {
    if (this.shouldClose === null) {
      this.shouldClose = true
    }

    if (this.shouldClose) {
      if (this.ownerHandlesClose()) {
        this.requestClose(event)
      } else {
        this.focusContent()
      }
    }
    this.shouldClose = null
  }

  handleContentOnMouseUp = () => {
    this.shouldClose = false
  }

  handleContentOnClick = () => {
    this.shouldClose = false
  }

  handleContentOnMouseDown = () => {
    this.shouldClose = false
  }

  requestClose = (event) =>
    this.ownerHandlesClose() && this.props.onRequestClose(event)

  ownerHandlesClose = () => this.props.onRequestClose

  shouldBeClosed = () => !this.state.isOpen && !this.state.beforeClose

  contentHasFocus = () =>
    document.activeElement === this.content ||
    this.content.contains(document.activeElement)

  buildClassName = (which, additional) => {
    const classNames =
      typeof additional === 'object'
        ? additional
        : {
            base: CLASS_NAMES[which],
            afterOpen: `${CLASS_NAMES[which]}--after-open`,
            beforeClose: `${CLASS_NAMES[which]}--before-close`,
          }
    let className = classNames.base
    if (this.state.afterOpen) {
      className = `${className} ${classNames.afterOpen}`
    }
    if (this.state.beforeClose) {
      className = `${className} ${classNames.beforeClose}`
    }
    return typeof additional === 'string' && additional
      ? `${className} ${additional}`
      : className
  }

  attributesFromObject = (prefix, items) =>
    Object.keys(items).reduce((acc, name) => {
      acc[`${prefix}-${name}`] = items[name]
      return acc
    }, {})

  render() {
    const { id, className, overlayClassName, defaultStyles, children } =
      this.props
    const contentStyles = className ? {} : defaultStyles.content
    const overlayStyles = overlayClassName ? {} : defaultStyles.overlay

    if (this.shouldBeClosed()) {
      return null
    }

    const overlayProps = {
      ref: this.setOverlayRef,
      className: this.buildClassName('overlay', overlayClassName),
      style: { ...overlayStyles, ...this.props.style.overlay },
      onClick: this.handleOverlayOnClick,
    }

    const contentProps = {
      id,
      ref: this.setContentRef,
      style: { ...contentStyles, ...this.props.style.content },
      className: this.buildClassName('content', className),
      tabIndex: '-1',
      onKeyDown: this.handleKeyDown,
      onMouseDown: this.handleContentOnMouseDown,
      onMouseUp: this.handleContentOnMouseUp,
      onClick: this.handleContentOnClick,
      role: 'dialog',
      'aria-label': this.props.contentLabel,
      ...this.attributesFromObject('aria', { modal: true, ...this.props.aria }),
      ...this.attributesFromObject('data', this.props.data || {}),
    }

    const contentElement = <div {...contentProps}>{children}</div>
    return <div {...overlayProps}>{contentElement}</div>
  }
}
